// Shape and template definitions for SVG Studio.
import { nanoIdLike } from "./utils";
import { DEFAULT_ELEMENT_COLOR } from "./drawing";

const baseAttr = (overrides) => ({
  fill: DEFAULT_ELEMENT_COLOR,
  stroke: "#0a0a0a",
  strokeWidth: 0,
  opacity: 1,
  ...overrides,
});

const polyPoints = (cx, cy, sides, R, rot = -Math.PI / 2) => {
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const ang = rot + (i * 2 * Math.PI) / sides;
    pts.push(`${(cx + R * Math.cos(ang)).toFixed(1)},${(cy + R * Math.sin(ang)).toFixed(1)}`);
  }
  return pts.join(" ");
};

export const BASIC_SHAPES = [
  { id: "rect", label: "Rect", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "rect", attrs: baseAttr({ x, y, width: 140, height: 90, rx: 0 }), animation: null }) },
  { id: "circle", label: "Circle", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx: x + 50, cy: y + 50, r: 50 }), animation: null }) },
  { id: "ellipse", label: "Ellipse", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "ellipse", attrs: baseAttr({ cx: x + 70, cy: y + 45, rx: 70, ry: 45 }), animation: null }) },
  { id: "line", label: "Line", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "line", attrs: baseAttr({ x1: x, y1: y, x2: x + 160, y2: y, stroke: DEFAULT_ELEMENT_COLOR, strokeWidth: 3, fill: "none" }), animation: null }) },
  { id: "polygon", label: "Triangle", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "polygon", attrs: baseAttr({ points: `${x + 60},${y} ${x},${y + 120} ${x + 120},${y + 120}` }), animation: null }) },
  {
    id: "star",
    label: "Star",
    create: (x = 100, y = 100) => {
      const cx = x + 60, cy = y + 60, R = 55, r = 22;
      const pts = [];
      for (let i = 0; i < 10; i++) {
        const ang = (Math.PI / 5) * i - Math.PI / 2;
        const rad = i % 2 === 0 ? R : r;
        pts.push(`${cx + rad * Math.cos(ang)},${cy + rad * Math.sin(ang)}`);
      }
      return { id: nanoIdLike(), type: "polygon", attrs: baseAttr({ points: pts.join(" "), fill: "#FFB400" }), animation: null };
    },
  },
  { id: "hexagon", label: "Hexagon", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "polygon", attrs: baseAttr({ points: polyPoints(x + 60, y + 60, 6, 56), fill: "#002FA7" }), animation: null }) },
  { id: "pentagon", label: "Pentagon", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "polygon", attrs: baseAttr({ points: polyPoints(x + 60, y + 60, 5, 56), fill: "#10b981" }), animation: null }) },
  {
    id: "heart",
    label: "Heart",
    create: (x = 100, y = 100) => ({
      id: nanoIdLike(),
      type: "path",
      attrs: baseAttr({ d: `M ${x + 60} ${y + 110} C ${x - 10} ${y + 55}, ${x + 18} ${y}, ${x + 60} ${y + 40} C ${x + 102} ${y}, ${x + 130} ${y + 55}, ${x + 60} ${y + 110} Z`, fill: "#FF2A00", strokeWidth: 0 }),
      animation: null,
    }),
  },
  {
    id: "arrow",
    label: "Arrow",
    create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "polygon", attrs: baseAttr({ points: `${x},${y + 35} ${x + 90},${y + 35} ${x + 90},${y + 15} ${x + 140},${y + 50} ${x + 90},${y + 85} ${x + 90},${y + 65} ${x},${y + 65}`, fill: "#002FA7" }), animation: null }),
  },
  { id: "text", label: "Text", create: (x = 100, y = 100) => ({ id: nanoIdLike(), type: "text", attrs: { x: x + 10, y: y + 40, fill: "#0a0a0a", fontSize: 32, fontFamily: "IBM Plex Sans", text: "Hello SVG" }, animation: null }) },
];

export const TEMPLATES = [
  {
    id: "loader-dots",
    label: "Loader · Dots",
    create: (x = 100, y = 100) => [0, 0.15, 0.3].map((d, i) => ({
      id: nanoIdLike(), type: "circle",
      attrs: baseAttr({ cx: x + 20 + i * 40, cy: y + 40, r: 12, fill: "#002FA7" }),
      animation: { type: "scale", duration: 1, delay: d, easing: "ease-in-out", repeat: "infinite", from: 0.5, to: 1.3 },
    })),
  },
  {
    // Robust spinner: a full track circle plus a foreground circle whose
    // stroke-dasharray reveals only an arc. Rotating the *whole circle* around
    // its own centre (transform-box:fill-box, origin:center) keeps the pivot
    // correct under any pan/zoom — unlike a partial arc-path with an explicit
    // pixel origin, which drifts off-screen when the canvas is transformed.
    id: "spinner-arc",
    label: "Spinner · Arc",
    create: (x = 100, y = 100) => {
      const cx = x + 60, cy = y + 60, r = 40;
      const C = 2 * Math.PI * r;
      return [
        { id: nanoIdLike(), type: "circle", attrs: { cx, cy, r, fill: "none", stroke: "#e5e5e5", strokeWidth: 8, opacity: 1 }, animation: null },
        { id: nanoIdLike(), type: "circle", attrs: { cx, cy, r, fill: "none", stroke: "#002FA7", strokeWidth: 8, strokeLinecap: "round", strokeDasharray: `${(C * 0.3).toFixed(1)} ${C.toFixed(1)}`, opacity: 1 }, animation: { type: "rotate", duration: 0.9, delay: 0, easing: "linear", repeat: "infinite", from: 0, to: 360 } },
      ];
    },
  },
  {
    // Two counter-rotating dashed rings — a classic, GPU-friendly loader.
    id: "dual-ring",
    label: "Spinner · Dual Ring",
    create: (x = 100, y = 100) => {
      const cx = x + 60, cy = y + 60;
      const ro = 46, ri = 30;
      const Co = 2 * Math.PI * ro, Ci = 2 * Math.PI * ri;
      return [
        { id: nanoIdLike(), type: "circle", attrs: { cx, cy, r: ro, fill: "none", stroke: "#002FA7", strokeWidth: 6, strokeLinecap: "round", strokeDasharray: `${(Co * 0.25).toFixed(1)} ${Co.toFixed(1)}`, opacity: 1 }, animation: { type: "rotate", duration: 1.1, delay: 0, easing: "linear", repeat: "infinite", from: 0, to: 360 } },
        { id: nanoIdLike(), type: "circle", attrs: { cx, cy, r: ri, fill: "none", stroke: "#FF6B00", strokeWidth: 6, strokeLinecap: "round", strokeDasharray: `${(Ci * 0.25).toFixed(1)} ${Ci.toFixed(1)}`, opacity: 1 }, animation: { type: "rotate", duration: 1.1, delay: 0, easing: "linear", repeat: "infinite", direction: "reverse", from: 0, to: 360 } },
      ];
    },
  },
  {
    id: "pulse-ring",
    label: "Pulse · Ring",
    create: (x = 100, y = 100) => [
      { id: nanoIdLike(), type: "circle", attrs: { cx: x + 60, cy: y + 60, r: 18, fill: "#002FA7", opacity: 1 }, animation: { type: "scale", duration: 1.6, delay: 0, easing: "ease-in-out", repeat: "infinite", from: 0.9, to: 1.15 } },
      { id: nanoIdLike(), type: "circle", attrs: { cx: x + 60, cy: y + 60, r: 28, fill: "none", stroke: "#002FA7", strokeWidth: 4, opacity: 1 }, animation: { type: "scale", duration: 1.6, delay: 0, easing: "ease-out", repeat: "infinite", from: 0.6, to: 2 } },
      { id: nanoIdLike(), type: "circle", attrs: { cx: x + 60, cy: y + 60, r: 28, fill: "none", stroke: "#7C3AED", strokeWidth: 4, opacity: 1 }, animation: { type: "scale", duration: 1.6, delay: 0.8, easing: "ease-out", repeat: "infinite", from: 0.6, to: 2 } },
    ],
  },
  {
    // Three balls bouncing with a staggered delay.
    id: "bouncing-balls",
    label: "Loader · Bounce",
    create: (x = 100, y = 100) => [0, 0.16, 0.32].map((d, i) => ({
      id: nanoIdLike(), type: "circle",
      attrs: baseAttr({ cx: x + 22 + i * 38, cy: y + 70, r: 12, fill: i === 1 ? "#FF6B00" : "#002FA7" }),
      animation: { type: "translateY", duration: 0.6, delay: d, easing: "cubic-bezier(.5,.05,.5,.95)", repeat: "infinite", direction: "alternate", to: -34 },
    })),
  },
  {
    // Indeterminate progress bar: a fill rect that grows from the left edge.
    id: "progress-bar",
    label: "Progress · Bar",
    create: (x = 100, y = 100) => [
      { id: nanoIdLike(), type: "rect", attrs: baseAttr({ x, y: y + 50, width: 220, height: 14, rx: 7, fill: "#e5e5e5" }), animation: null },
      { id: nanoIdLike(), type: "rect", attrs: baseAttr({ x, y: y + 50, width: 220, height: 14, rx: 7, fill: "#002FA7" }), animation: { type: "scaleX", duration: 1.6, delay: 0, easing: "cubic-bezier(.65,0,.35,1)", repeat: "infinite", origin: "left", from: 0.05, to: 1 } },
    ],
  },
  {
    id: "gradient-orb",
    label: "Orb · Gradient",
    create: (x = 100, y = 100) => [
      { id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx: x + 60, cy: y + 60, r: 54, fill: "#002FA7" }), effects: { fill: { kind: "radial", to: "#7C3AED" }, filter: { kind: "glow", strength: 8, color: "#7C3AED" } }, animation: { type: "scale", duration: 3, delay: 0, easing: "ease-in-out", repeat: "infinite", from: 0.95, to: 1.08 } },
    ],
  },
  {
    // A dot orbiting a fixed hub — orbit keyframes rotate around the dot's own
    // centre so it traces a clean circular path.
    id: "orbit-loader",
    label: "Loader · Orbit",
    create: (x = 100, y = 100) => {
      const cx = x + 60, cy = y + 60;
      return [
        { id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx, cy, r: 8, fill: "#0a0a0a" }), animation: null },
        { id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx, cy, r: 10, fill: "#002FA7" }), animation: { type: "orbit", duration: 1.4, delay: 0, easing: "linear", repeat: "infinite", to: 42 } },
        { id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx, cy, r: 10, fill: "#FF6B00" }), animation: { type: "orbit", duration: 1.4, delay: 0.7, easing: "linear", repeat: "infinite", to: 42 } },
      ];
    },
  },
  {
    // Spinning gear / cog.
    id: "gear-spin",
    label: "Spinner · Gear",
    create: (x = 100, y = 100) => {
      const cx = x + 60, cy = y + 60;
      const teeth = 12, R = 52, r = 40, ir = 18;
      const pts = [];
      for (let i = 0; i < teeth * 2; i++) {
        const ang = (Math.PI / teeth) * i - Math.PI / 2;
        const rad = i % 2 === 0 ? R : r;
        pts.push(`${(cx + rad * Math.cos(ang)).toFixed(1)},${(cy + rad * Math.sin(ang)).toFixed(1)}`);
      }
      return [
        { id: nanoIdLike(), type: "polygon", attrs: baseAttr({ points: pts.join(" "), fill: "#FFB400" }), animation: { type: "rotate", duration: 4, delay: 0, easing: "linear", repeat: "infinite", from: 0, to: 360 } },
        { id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx, cy, r: ir, fill: "#0a0a0a" }), animation: { type: "rotate", duration: 4, delay: 0, easing: "linear", repeat: "infinite", from: 0, to: 360 } },
      ];
    },
  },
  {
    // Three concentric dashed rings rotating at different speeds — a techy,
    // radar-like indicator.
    id: "concentric-rotate",
    label: "Spinner · Radar",
    create: (x = 100, y = 100) => {
      const cx = x + 60, cy = y + 60;
      return [44, 32, 20].map((rad, i) => {
        const C = 2 * Math.PI * rad;
        return {
          id: nanoIdLike(), type: "circle",
          attrs: { cx, cy, r: rad, fill: "none", stroke: ["#002FA7", "#7C3AED", "#FF6B00"][i], strokeWidth: 4, strokeLinecap: "round", strokeDasharray: `${(C * (0.4 - i * 0.08)).toFixed(1)} ${C.toFixed(1)}`, opacity: 1 },
          animation: { type: "rotate", duration: 1.2 + i * 0.6, delay: 0, easing: "linear", repeat: "infinite", direction: i % 2 ? "reverse" : "normal", from: 0, to: 360 },
        };
      });
    },
  },
  {
    id: "badge-check",
    label: "Badge · Check",
    create: (x = 100, y = 100) => [
      { id: nanoIdLike(), type: "circle", attrs: baseAttr({ cx: x + 60, cy: y + 60, r: 50, fill: "#10b981" }), animation: { type: "popIn", duration: 0.6, delay: 0, easing: "cubic-bezier(.34,1.56,.64,1)", repeat: "1" } },
      { id: nanoIdLike(), type: "path", attrs: { d: `M ${x + 38} ${y + 62} L ${x + 56} ${y + 80} L ${x + 86} ${y + 44}`, fill: "none", stroke: "#ffffff", strokeWidth: 7, strokeLinecap: "round", strokeLinejoin: "round", opacity: 1 }, animation: { type: "draw", duration: 0.8, delay: 0.4, easing: "ease-out", repeat: "1" } },
    ],
  },
  {
    id: "heartbeat",
    label: "Pulse · Heart",
    create: (x = 100, y = 100) => [
      { id: nanoIdLike(), type: "path", attrs: baseAttr({ d: `M ${x + 60} ${y + 100} C ${x + 4} ${y + 56}, ${x + 26} ${y + 8}, ${x + 60} ${y + 38} C ${x + 94} ${y + 8}, ${x + 116} ${y + 56}, ${x + 60} ${y + 100} Z`, fill: "#FF2A00", strokeWidth: 0 }), animation: { type: "heartbeat", duration: 1.3, delay: 0, easing: "ease-in-out", repeat: "infinite" } },
    ],
  },
  {
    id: "wave-bars",
    label: "Wave · Bars",
    create: (x = 100, y = 100) => Array.from({ length: 5 }, (_, i) => ({
      id: nanoIdLike(), type: "rect",
      attrs: baseAttr({ x: x + i * 16, y: y + 30, width: 10, height: 60, fill: "#002FA7", rx: 2 }),
      animation: { type: "scaleY", duration: 0.9, delay: i * 0.1, easing: "ease-in-out", repeat: "infinite", from: 0.3, to: 1, origin: "bottom" },
    })),
  },
  {
    id: "card-banner",
    label: "Card · Banner",
    create: (x = 100, y = 100) => [
      { id: nanoIdLike(), type: "rect", attrs: baseAttr({ x, y, width: 260, height: 120, fill: "#0a0a0a" }), animation: null },
      { id: nanoIdLike(), type: "rect", attrs: baseAttr({ x: x + 14, y: y + 14, width: 4, height: 92, fill: "#FFB400" }), animation: null },
      { id: nanoIdLike(), type: "text", attrs: { x: x + 30, y: y + 50, fill: "#ffffff", fontSize: 22, fontFamily: "IBM Plex Mono", text: "SVG STUDIO" }, animation: { type: "fadeUp", duration: 0.7, delay: 0.1, easing: "ease-out", repeat: "1" } },
      { id: nanoIdLike(), type: "text", attrs: { x: x + 30, y: y + 86, fill: "#a3a3a3", fontSize: 13, fontFamily: "IBM Plex Sans", text: "Interactive vector workshop" }, animation: { type: "fadeUp", duration: 0.7, delay: 0.25, easing: "ease-out", repeat: "1" } },
    ],
  },
];

// Type-specific default parameters for each animation type. Shared by the
// left-panel presets (ANIMATION_PRESETS.build) and the right-panel type
// selector (AnimationControls.onTypeChange) so both entry points produce
// identical animation objects for the same type — preventing the
// "same effect looks different depending on where it was applied" bug.
// Only type-specific fields are listed here; common fields (duration/delay/
// easing/repeat/direction/origin/fillMode) are merged in by defaultAnimationFor.
export const ANIM_DEFAULTS = {
  fade: { from: 0, to: 1 },
  fadeUp: { to: 24 },
  fadeDown: { to: 24 },
  fadeLeft: { to: 24 },
  fadeRight: { to: 24 },
  slideInLeft: {},
  slideInRight: {},
  popIn: {},
  bounceIn: {},
  elastic: {},
  zoomIn: { from: 0.2 },
  zoomOut: { to: 0.2 },
  rotate: { from: 0, to: 360 },
  scale: { from: 0.8, to: 1.2 },
  translateY: { to: -30 },
  translateX: { to: 20 },
  float: { to: -14 },
  orbit: { to: 30 },
  swing: { to: 15, origin: "top" },
  wobble: { to: 12 },
  jello: {},
  heartbeat: {},
  rubberBand: {},
  tada: {},
  flip: {},
  blink: { to: 0.15 },
  skew: { to: 12 },
  colorShift: { fromColor: "#002FA7", toColor: "#FF2A00", direction: "alternate" },
  strokeShift: { fromColor: "#FF6B00", toColor: "#7C3AED", direction: "alternate" },
  morphHue: {},
  draw: {},
  drawReverse: {},
  drawOn: {},
  dash: { to: -200, dasharray: "10 8" },
  shake: { from: -8, to: 8 },
  flash: {},
  vibrate: {},
  glitch: {},
  breathe: {},
  jiggle: {},
  wave: {},
  pendulum: { to: 18, origin: "top" },
  rollIn: {},
  spiralIn: {},
  dropBounce: {},
  flipInX: {},
  flipInY: {},
  cube3d: {},
  blurIn: {},
  blurPulse: {},
  neonGlow: { glowColor: "#00E5FF" },
  brightnessPulse: {},
  saturatePulse: {},
  scaleX: { from: 0.05, to: 1 },
  scaleY: { from: 0.3, to: 1 },
  spin3d: { to: 360 },
  flipX: {},
};

// Per-type common-field defaults. Most types use the same common defaults; a
// few (rotate/orbit/morphHue are linear loops; intro effects play once) need
// different duration/easing/repeat. These mirror the ANIMATION_PRESETS values
// so the right-panel selector matches the left-panel tiles exactly.
const ANIM_COMMON_BY_TYPE = {
  fade: { duration: 1, easing: "ease-out", repeat: "1" },
  fadeUp: { duration: 0.8, easing: "ease-out", repeat: "1" },
  fadeDown: { duration: 0.8, easing: "ease-out", repeat: "1" },
  fadeLeft: { duration: 0.8, easing: "ease-out", repeat: "1" },
  fadeRight: { duration: 0.8, easing: "ease-out", repeat: "1" },
  slideInLeft: { duration: 0.7, easing: "cubic-bezier(.34,1.56,.64,1)", repeat: "1" },
  slideInRight: { duration: 0.7, easing: "cubic-bezier(.34,1.56,.64,1)", repeat: "1" },
  popIn: { duration: 0.6, easing: "cubic-bezier(.34,1.56,.64,1)", repeat: "1" },
  bounceIn: { duration: 1.0, easing: "ease-out", repeat: "1" },
  elastic: { duration: 1.2, easing: "ease-out", repeat: "1" },
  zoomIn: { duration: 0.6, easing: "ease-out", repeat: "1" },
  zoomOut: { duration: 0.6, easing: "ease-out", repeat: "1" },
  rotate: { duration: 2, easing: "linear", repeat: "infinite" },
  scale: { duration: 1.2, easing: "ease-in-out", repeat: "infinite" },
  translateY: { duration: 1, easing: "ease-in-out", repeat: "infinite" },
  translateX: { duration: 1.4, easing: "ease-in-out", repeat: "infinite" },
  float: { duration: 3, easing: "ease-in-out", repeat: "infinite" },
  orbit: { duration: 3, easing: "linear", repeat: "infinite" },
  swing: { duration: 1.4, easing: "ease-in-out", repeat: "infinite" },
  wobble: { duration: 1, easing: "ease-in-out", repeat: "infinite" },
  jello: { duration: 1, easing: "ease-in-out", repeat: "infinite" },
  heartbeat: { duration: 1.3, easing: "ease-in-out", repeat: "infinite" },
  rubberBand: { duration: 1, easing: "ease-in-out", repeat: "infinite" },
  tada: { duration: 1, easing: "ease-in-out", repeat: "infinite" },
  flip: { duration: 1.6, easing: "ease-in-out", repeat: "infinite" },
  blink: { duration: 1, easing: "steps(1)", repeat: "infinite" },
  skew: { duration: 1.4, easing: "ease-in-out", repeat: "infinite" },
  colorShift: { duration: 2, easing: "ease-in-out", repeat: "infinite" },
  strokeShift: { duration: 2, easing: "ease-in-out", repeat: "infinite" },
  morphHue: { duration: 4, easing: "linear", repeat: "infinite" },
  draw: { duration: 1.4, easing: "ease-out", repeat: "1" },
  drawReverse: { duration: 1.4, easing: "ease-out", repeat: "1" },
  drawOn: { duration: 1, easing: "ease", repeat: "infinite" },
  dash: { duration: 1, easing: "linear", repeat: "infinite" },
  shake: { duration: 0.6, easing: "ease-in-out", repeat: "infinite" },
  flash: { duration: 1.2, easing: "linear", repeat: "infinite" },
  vibrate: { duration: 0.35, easing: "linear", repeat: "infinite" },
  glitch: { duration: 0.5, easing: "steps(3,end)", repeat: "infinite" },
  breathe: { duration: 3, easing: "ease-in-out", repeat: "infinite" },
  jiggle: { duration: 0.8, easing: "ease-in-out", repeat: "infinite" },
  wave: { duration: 2, easing: "ease-in-out", repeat: "infinite" },
  pendulum: { duration: 2, easing: "ease-in-out", repeat: "infinite" },
  rollIn: { duration: 0.9, easing: "cubic-bezier(.22,1,.36,1)", repeat: "1" },
  spiralIn: { duration: 1, easing: "cubic-bezier(.22,1,.36,1)", repeat: "1" },
  dropBounce: { duration: 1.2, easing: "ease-out", repeat: "1" },
  flipInX: { duration: 0.9, easing: "ease-out", repeat: "1" },
  flipInY: { duration: 0.9, easing: "ease-out", repeat: "1" },
  cube3d: { duration: 3, easing: "ease-in-out", repeat: "infinite" },
  blurIn: { duration: 0.9, easing: "ease-out", repeat: "1" },
  blurPulse: { duration: 1.8, easing: "ease-in-out", repeat: "infinite" },
  neonGlow: { duration: 1.6, easing: "ease-in-out", repeat: "infinite" },
  brightnessPulse: { duration: 1.6, easing: "ease-in-out", repeat: "infinite" },
  saturatePulse: { duration: 1.6, easing: "ease-in-out", repeat: "infinite" },
  scaleX: { duration: 1.6, easing: "cubic-bezier(.65,0,.35,1)", repeat: "infinite", origin: "left" },
  scaleY: { duration: 0.9, easing: "ease-in-out", repeat: "infinite", origin: "bottom" },
  spin3d: { duration: 1.6, easing: "linear", repeat: "infinite" },
  flipX: { duration: 1.6, easing: "ease-in-out", repeat: "infinite" },
};

// Build a fresh, complete animation object for the given type. Used by both
// the left-panel preset tiles and the right-panel type selector so the two
// entry points always produce identical configs for the same type.
// Monotonic counter stamped on every newly-created animation object. The
// animation engine includes this nonce in CSS class/keyframes names so the
// browser re-triggers the animation whenever a NEW animation object is
// applied — even if the type is unchanged (e.g. clicking the same preset
// twice). Without it, switching from fade → fadeUp keeps the same
// `animation-name` and the browser treats it as a parameter update, never
// replaying. Parameter tweaks (duration/delay/easing/…) go through setAnim
// which merges into the existing object, preserving the old nonce, so they
// do NOT trigger a replay — only a type change or re-apply does.
let _animNonce = 0;

// `keepCommon` (optional) carries over user-adjusted common fields (duration/
// delay/easing/repeat/direction/fillMode) from a previous animation when
// switching types in the right panel — only type-specific fields are reset.
export function defaultAnimationFor(type, keepCommon) {
  const common = {
    duration: 1,
    delay: 0,
    easing: "ease-out",
    repeat: "infinite",
    direction: "normal",
    origin: "center",
    fillMode: "both",
    ...(ANIM_COMMON_BY_TYPE[type] || {}),
    ...(keepCommon || {}),
  };
  return { type, ...common, ...(ANIM_DEFAULTS[type] || {}), _n: ++_animNonce };
}

export const ANIMATION_PRESETS = [
  { id: "fade", label: "Fade In", build: () => defaultAnimationFor("fade") },
  { id: "fadeUp", label: "Fade Up", build: () => defaultAnimationFor("fadeUp") },
  { id: "fadeDown", label: "Fade Down", build: () => defaultAnimationFor("fadeDown") },
  { id: "slideInLeft", label: "Slide In ←", build: () => defaultAnimationFor("slideInLeft") },
  { id: "slideInRight", label: "Slide In →", build: () => defaultAnimationFor("slideInRight") },
  { id: "popIn", label: "Pop In", build: () => defaultAnimationFor("popIn") },
  { id: "bounceIn", label: "Bounce In", build: () => defaultAnimationFor("bounceIn") },
  { id: "elastic", label: "Elastic", build: () => defaultAnimationFor("elastic") },
  { id: "zoomIn", label: "Zoom In", build: () => defaultAnimationFor("zoomIn") },
  { id: "rotate", label: "Spin", build: () => defaultAnimationFor("rotate") },
  { id: "scale", label: "Pulse", build: () => defaultAnimationFor("scale") },
  { id: "bounce", label: "Bounce", build: () => defaultAnimationFor("translateY") },
  { id: "float", label: "Float", build: () => defaultAnimationFor("float") },
  { id: "orbit", label: "Orbit", build: () => defaultAnimationFor("orbit") },
  { id: "swing", label: "Swing", build: () => defaultAnimationFor("swing") },
  { id: "wobble", label: "Wobble", build: () => defaultAnimationFor("wobble") },
  { id: "jello", label: "Jello", build: () => defaultAnimationFor("jello") },
  { id: "heartbeat", label: "Heartbeat", build: () => defaultAnimationFor("heartbeat") },
  { id: "rubberBand", label: "Rubber Band", build: () => defaultAnimationFor("rubberBand") },
  { id: "tada", label: "Tada", build: () => defaultAnimationFor("tada") },
  { id: "flip", label: "Flip Y", build: () => defaultAnimationFor("flip") },
  { id: "blink", label: "Blink", build: () => defaultAnimationFor("blink") },
  { id: "skew", label: "Skew", build: () => defaultAnimationFor("skew") },
  { id: "colorShift", label: "Color Shift", build: () => defaultAnimationFor("colorShift") },
  { id: "strokeShift", label: "Stroke Shift", build: () => defaultAnimationFor("strokeShift") },
  { id: "morphHue", label: "Hue Cycle", build: () => defaultAnimationFor("morphHue") },
  { id: "draw", label: "Draw Path", build: () => defaultAnimationFor("draw") },
  { id: "drawReverse", label: "Undraw Path", build: () => defaultAnimationFor("drawReverse") },
  { id: "dash", label: "March", build: () => defaultAnimationFor("dash") },
  { id: "shake", label: "Shake", build: () => defaultAnimationFor("shake") },
  { id: "flash", label: "Flash", build: () => defaultAnimationFor("flash") },
  { id: "vibrate", label: "Vibrate", build: () => defaultAnimationFor("vibrate") },
  { id: "glitch", label: "Glitch", build: () => defaultAnimationFor("glitch") },
  { id: "breathe", label: "Breathe", build: () => defaultAnimationFor("breathe") },
  { id: "jiggle", label: "Jiggle", build: () => defaultAnimationFor("jiggle") },
  { id: "wave", label: "Wave", build: () => defaultAnimationFor("wave") },
  { id: "pendulum", label: "Pendulum", build: () => defaultAnimationFor("pendulum") },
  { id: "rollIn", label: "Roll In", build: () => defaultAnimationFor("rollIn") },
  { id: "spiralIn", label: "Spiral In", build: () => defaultAnimationFor("spiralIn") },
  { id: "dropBounce", label: "Drop In", build: () => defaultAnimationFor("dropBounce") },
  { id: "flipInX", label: "Flip In X", build: () => defaultAnimationFor("flipInX") },
  { id: "flipInY", label: "Flip In Y", build: () => defaultAnimationFor("flipInY") },
  { id: "cube3d", label: "Cube 3D", build: () => defaultAnimationFor("cube3d") },
  { id: "blurIn", label: "Blur In", build: () => defaultAnimationFor("blurIn") },
  { id: "blurPulse", label: "Blur Pulse", build: () => defaultAnimationFor("blurPulse") },
  { id: "neonGlow", label: "Neon Glow", build: () => defaultAnimationFor("neonGlow") },
  { id: "brightnessPulse", label: "Brightness", build: () => defaultAnimationFor("brightnessPulse") },
  { id: "saturatePulse", label: "Saturate", build: () => defaultAnimationFor("saturatePulse") },
];
